/**
 * Persisted browser device identity (single-owner storage-key
 * rule).
 *
 * Each product creates exactly ONE store from its own app-owned storage key
 * (`aegix_one_device_id`, `aegix_sms_device_id`, AIM's legacy `device_id`) —
 * that store's module is the only minter, everything else reads. Products
 * may share an eTLD+1 (or an origin) in some deployments and must never
 * fight over one `device_id` slot.
 *
 * The value rides the `x-device-id` header + `device_id` payload field on
 * FCM register/deregister so the backend's device-continuity rules
 * (stale-registration sweep + cross-user hand-off) can key on a stable
 * per-browser identity that outlives the session.
 */

export interface DeviceIdStore {
  /**
   * The persisted device id, minting one on first use. Registration always
   * has an identity to send; when storage is unavailable (Safari private
   * mode, partitioned iframes) the configured `ephemeralFallback` applies —
   * an unstable identity only weakens the backend's device sweep, which is
   * a state it handles, while registering with no identity forfeits
   * per-device scoping entirely.
   */
  getOrCreate(): string;
  /**
   * Read-only view for callers that must NOT invent an identity (e.g.
   * deregistration, which addresses the row registered under the ORIGINAL
   * id — minting a fresh id there could never match). `null` when this
   * browser has none persisted.
   */
  getPersisted(): string | null;
}

export interface DeviceIdStoreOptions {
  /** App-owned storage key. Exactly one store per key (single-owner rule). */
  storageKey: string;
  /**
   * Fallback when localStorage is unavailable:
   *  - `'stable'`: one ephemeral id per store lifetime (page lifetime) —
   *    ONE's behavior; `getPersisted()` returns it once minted.
   *  - `'per-call'`: a fresh mint per `getOrCreate()` call — SMS's
   *    behavior; `getPersisted()` returns `null`.
   */
  ephemeralFallback: 'stable' | 'per-call';
}

/** The shared web device-id mint format. */
export function mintWebDeviceId(): string {
  return `web-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

export function createDeviceIdStore(
  options: DeviceIdStoreOptions,
): DeviceIdStore {
  const { storageKey, ephemeralFallback } = options;

  /** Session-lifetime fallback when localStorage is unavailable ('stable'). */
  let ephemeralDeviceId: string | null = null;

  function ephemeral(): string {
    if (ephemeralFallback === 'per-call') return mintWebDeviceId();
    if (!ephemeralDeviceId) ephemeralDeviceId = mintWebDeviceId();
    return ephemeralDeviceId;
  }

  return {
    getOrCreate(): string {
      try {
        const existing = localStorage.getItem(storageKey)?.trim();
        if (existing) return existing;
        const minted = mintWebDeviceId();
        localStorage.setItem(storageKey, minted);
        return minted;
      } catch {
        return ephemeral();
      }
    },
    getPersisted(): string | null {
      try {
        const deviceId = localStorage.getItem(storageKey)?.trim();
        return deviceId ? deviceId : null;
      } catch {
        return ephemeralFallback === 'stable' ? ephemeralDeviceId : null;
      }
    },
  };
}
